// angular
import {
  AfterViewInit,
  Component,
  effect,
  ElementRef,
  inject,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
// components
import { InputComponent } from '@app/core/components/input/input.component';
// services
import { MessagingService } from '@app/+chat/shared/services/messaging.service';
import { StateManagementService } from '@app/+chat/shared/services/state-management.service';
import { UserService } from '@app/shared/services/user.service';
// interfaces & types
import { IMessage } from '@app/+chat/shared/interfaces';
import { IUser } from '@app/shared/types/user.type';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'pwj-chat-input',
  standalone: true,
  imports: [ReactiveFormsModule, FormsModule, TranslateModule],
  templateUrl: './chat-input.component.html',
  styleUrl: './chat-input.component.scss',
})
export class ChatInputComponent implements AfterViewInit {
  // message editable div element
  @ViewChild('messageBox', { read: ElementRef }) public messageBox!: ElementRef;

  public messageForm!: FormGroup;
  public chatId!: string;

  private _userData!: IUser;
  private _allMessagesData: IMessage[] = [];
  private _messagesCount!: number;

  // dependencies
  private readonly _messageService = inject(MessagingService);
  private readonly _stateManagementService = inject(StateManagementService);
  private readonly _userService = inject(UserService);
  private readonly _fb = inject(FormBuilder);

  constructor() {
    // initialize message form
    this.messageForm = this._fb.group({
      message: [''],
    });

    // set user data
    effect(() => {
      this._userData = this._userService.userSignal();
    });

    // set suggestion message as input value on select
    effect(() => {
      const suggestedMessage =
        this._stateManagementService.selectedSuggestionMessage();

      if (suggestedMessage && suggestedMessage?.length > 0) {
        this.setSuggestedMessage(suggestedMessage);
      }
    });

    // set chat id
    effect(() => {
      this.chatId = this._stateManagementService.oneChatById()?._id as string;
    });

    // set messages data
    effect(() => {
      this._allMessagesData = this._stateManagementService.allMessages();
    });

    // set messages count
    effect(() => {
      if (this._stateManagementService.allMessagesCount()) {
        this._messagesCount =
          this._stateManagementService.allMessagesCount() as number;
      }
    });
  }

  ngAfterViewInit() {
    this.messageBox.nativeElement.addEventListener(
      'keydown',
      this.handleKeyDown
    );
  }

  public onTypeMessage(event: HTMLElement) {
    this.messageForm.patchValue({
      message: event.textContent,
    });
  }

  public sendMessage() {
    const body = {
      ...this.messageForm.value,
      chat_id: this.chatId,
    };

    if (body.message) {
      this._messageService.newMessage(body);

      const unSendedMessage: IMessage = {
        ...body,
        created_at: new Date(),
        updated_at: new Date(),
        message: {
          [this._stateManagementService.currentChatLanguage()]: body.message
        },
        is_message_posted: false,
        sender: this._userData?.kid
          ? {
              _id: this._userData?._id,
              first_name: this._userData?.first_name,
              last_name: this._userData?.last_name,
              kid: this._userData?.kid,
            }
          : null,
      };

      // update messages list
      const updatedMessagesData = [...this?._allMessagesData, unSendedMessage];
      // set updated data
      this._stateManagementService.allMessages.set(updatedMessagesData);
      // update messages count
      this._stateManagementService.allMessagesCount.set(++this._messagesCount);
      this.messageForm.reset();
      this.messageBox.nativeElement.textContent = '';
    }
  }

  // create line break onEnter
  public handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !event.ctrlKey) {
      event.preventDefault();
      this.sendMessage();
    }
    if (event.key === 'Enter' && event.ctrlKey) {
      event.preventDefault();
      document.execCommand('insertHTML', false, '<br><br>');
    }
  };

  public setSuggestedMessage(message: string) {
    this.messageBox.nativeElement.textContent = message;
    this.messageForm.patchValue({
      message: message,
    });
  }
}
